import React, { useEffect, useState } from 'react';
import { Plus, Eye, Pencil, Trash2, Search, X } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha } from '../utils/format';
import Modal from '../components/Modal';
import BotonAyuda from '../components/BotonAyuda';

function fechaHoy() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Sin tildes ni mayúsculas, para que "azucar" encuentre "Azúcar".
const norm = (t) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const ORDEN_TIPOS = ['materia_prima', 'intermedio', 'terminado'];
const LABEL_TIPO = { materia_prima: 'Materia prima', intermedio: 'Producto intermedio', terminado: 'Producto terminado' };
const tipoLabel = (t) => LABEL_TIPO[t] || t;

export default function InventarioCierre() {
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;

  const [productos, setProductos] = useState([]);
  const [cierres, setCierres] = useState([]);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [fecha, setFecha] = useState(fechaHoy());
  const [tipoDoc, setTipoDoc] = useState('cierre');
  // Documento en edición (elegido con el lápiz); null = documento nuevo.
  const [editando, setEditando] = useState(null);
  const [cantidades, setCantidades] = useState({});
  const [busqueda, setBusqueda] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [resumen, setResumen] = useState(null);
  const [cargandoResumen, setCargandoResumen] = useState(false);
  const [resumenId, setResumenId] = useState(null);
  const [tasaResumen, setTasaResumen] = useState('BCV');

  async function cargar() {
    const [prod, list] = await Promise.all([api.get('/productos'), api.get('/inventario-cierre')]);
    setProductos(prod.data);
    setCierres(list.data);
  }

  useEffect(() => {
    if (empresaActiva) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  // Siempre abre un documento nuevo y vacío: elegir una fecha que ya tiene
  // documentos no los carga ni los reemplaza (se guarda con el siguiente
  // correlativo de ese día).
  function abrirNuevo() {
    setError('');
    setResumen(null);
    setEditando(null);
    setFecha(fechaHoy());
    setTipoDoc('cierre');
    setCantidades({});
    setBusqueda('');
    setMostrarForm(true);
  }

  async function abrirEdicion(doc) {
    setError('');
    setResumen(null);
    try {
      const { data } = await api.get(`/inventario-cierre/${doc.id}`);
      const mapa = {};
      data.detalle.forEach((d) => { mapa[d.producto_id] = d.cantidad; });
      setEditando(doc);
      setFecha(String(doc.fecha).slice(0, 10));
      setTipoDoc(doc.tipo || 'cierre');
      setCantidades(mapa);
      setBusqueda('');
      setMostrarForm(true);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo abrir el documento');
    }
  }

  function cancelarForm() {
    setMostrarForm(false);
    setCantidades({});
    setBusqueda('');
    setEditando(null);
    setError('');
  }

  async function guardarCierre(e) {
    e.preventDefault();
    setError('');
    setGuardando(true);
    try {
      const detalle = Object.entries(cantidades)
        .filter(([, cant]) => cant !== '' && cant !== null && cant !== undefined)
        .map(([producto_id, cantidad]) => ({ producto_id: Number(producto_id), cantidad }));
      const { data } = editando
        ? await api.put(`/inventario-cierre/${editando.id}`, { tipo: tipoDoc, detalle })
        : await api.post('/inventario-cierre', { fecha, tipo: tipoDoc, detalle });
      setMostrarForm(false);
      setCantidades({});
      setEditando(null);
      await cargar();
      await verResumen(data.id);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar el cierre');
    } finally {
      setGuardando(false);
    }
  }

  async function verResumen(id, tasa) {
    setResumenId(id);
    const tasaAUsar = tasa || tasaResumen;
    setTasaResumen(tasaAUsar);
    setCargandoResumen(true);
    setResumen(null);
    try {
      const { data } = await api.get(`/inventario-cierre/${id}`, { params: { tasa: tasaAUsar } });
      setResumen(data);
    } finally {
      setCargandoResumen(false);
    }
  }

  async function eliminarCierre(id) {
    if (!window.confirm('¿Eliminar este documento de inventario? No se puede deshacer.')) return;
    try {
      await api.delete(`/inventario-cierre/${id}`);
      if (resumen?.cierre?.id === id) setResumen(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar el cierre');
    }
  }

  // El buscador solo oculta productos: las cantidades ya cargadas se guardan igual.
  const texto = norm(busqueda.trim());
  const productosPorTipo = productos
    .filter((p) => !texto || norm(p.nombre).includes(texto))
    .sort((a, b) => ORDEN_TIPOS.indexOf(a.tipo) - ORDEN_TIPOS.indexOf(b.tipo))
    .reduce((acc, p) => {
      const clave = tipoLabel(p.tipo);
      if (!acc[clave]) acc[clave] = [];
      acc[clave].push(p);
      return acc;
    }, {});

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-slate-800">
          Cierre de inventario
          {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
          <BotonAyuda />
        </h1>
        <button
          onClick={abrirNuevo}
          title="Nuevo cierre o ajuste"
          className="bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-md self-start sm:self-auto"
        >
          <Plus size={18} />
        </button>
      </div>

      {mostrarForm && (
        <Modal
          title={editando ? `Editar ${editando.tipo === 'ajuste' ? 'ajuste' : 'cierre'} ${editando.numero}` : 'Nuevo documento de inventario'}
          onClose={cancelarForm}
          maxWidth="max-w-4xl"
        >
        <form onSubmit={guardarCierre} className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm text-slate-600">Tipo</label>
            <select value={tipoDoc} onChange={(e) => setTipoDoc(e.target.value)} className="border rounded-md px-3 py-2">
              <option value="cierre">Cierre de inventario</option>
              <option value="ajuste">Ajuste</option>
            </select>
            <label className="text-sm text-slate-600">Fecha</label>
            <input
              type="date"
              value={fecha}
              disabled={!!editando}
              onChange={(e) => setFecha(e.target.value)}
              className="border rounded-md px-3 py-2 disabled:bg-slate-50 disabled:text-slate-500"
            />
          </div>
          <p className="text-xs text-slate-400">
            Cargá la cantidad final contada. Los productos que dejes vacíos no se incluyen
            {editando ? '.' : ' y el documento se guarda con el siguiente número del día; no reemplaza a ninguno anterior.'}
          </p>

          {productos.length > 0 && (
            <div className="flex items-center gap-2 border rounded-md px-3 py-2 max-w-sm">
              <Search size={14} className="text-slate-400 shrink-0" />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                placeholder="Buscar producto…"
                className="text-sm flex-1 outline-none min-w-0"
              />
              {busqueda && (
                <button type="button" onClick={() => setBusqueda('')} title="Limpiar búsqueda" className="text-slate-400 hover:text-slate-600">
                  <X size={14} />
                </button>
              )}
            </div>
          )}

          {productos.length === 0 ? (
            <p className="text-sm text-slate-400">No hay productos creados todavía. Cargalos primero en Productos.</p>
          ) : texto && Object.keys(productosPorTipo).length === 0 ? (
            <p className="text-sm text-slate-400">Ningún producto coincide con "{busqueda.trim()}". Las cantidades ya cargadas se mantienen.</p>
          ) : (
            Object.entries(productosPorTipo).map(([tipo, lista]) => (
              <div key={tipo}>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{tipo}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                  {lista.map((p) => (
                    <div key={p.id} className="flex items-center gap-2">
                      <span className="text-sm text-slate-600 flex-1 min-w-0 line-clamp-2 leading-snug" title={p.nombre}>{p.nombre}</span>
                      <input
                        type="number"
                        step="0.0001"
                        placeholder="0"
                        value={cantidades[p.id] ?? ''}
                        onChange={(e) => setCantidades({ ...cantidades, [p.id]: e.target.value })}
                        className="border rounded-md px-2 py-1.5 text-sm w-28 shrink-0"
                      />
                      <span className="text-xs text-slate-400 w-12 shrink-0">{p.unidad_medida}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <button
            type="submit"
            disabled={guardando || productos.length === 0}
            className="bg-primary-600 text-white rounded-md py-2 px-6 hover:bg-primary-700 disabled:opacity-50"
          >
            {guardando ? 'Guardando...' : editando ? 'Guardar cambios' : 'Guardar'}
          </button>
        </form>
        </Modal>
      )}

      {cargandoResumen && <p className="text-sm text-slate-400">Calculando valorización...</p>}

      {resumen && resumen.cierre && (
        <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-medium text-slate-600">
              {resumen.cierre.tipo === 'ajuste' ? 'Ajuste' : 'Cierre'} {resumen.cierre.numero} · valorizado al {formatFecha(resumen.cierre.fecha)}
            </p>
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-500">Tasa:</label>
              <select
                value={tasaResumen}
                onChange={(e) => verResumen(resumenId, e.target.value)}
                className="border rounded-md px-2 py-1 text-sm"
              >
                <option value="BCV">BCV</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="p-2">Tipo</th>
                  <th className="p-2 text-right">Valor a la fecha de cierre</th>
                  <th className="p-2 text-right">Valor a costo actual</th>
                  <th className="p-2 text-right">Diferencia</th>
                </tr>
              </thead>
              <tbody>
                {resumen.porTipo.map((c) => (
                  <tr key={c.tipo} className="border-t hover:bg-slate-50 transition-colors">
                    <td className="p-2">{c.tipo_label}</td>
                    <td className="p-2 text-right">${formatMonto(c.valor_cierre)}</td>
                    <td className="p-2 text-right">${formatMonto(c.valor_actual)}</td>
                    <td className={`p-2 text-right ${c.valor_actual - c.valor_cierre >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      ${formatMonto(c.valor_actual - c.valor_cierre)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t font-medium hover:bg-slate-50 transition-colors">
                  <td className="p-2">Total</td>
                  <td className="p-2 text-right">${formatMonto(resumen.totales.valor_cierre)}</td>
                  <td className="p-2 text-right">${formatMonto(resumen.totales.valor_actual)}</td>
                  <td className={`p-2 text-right ${resumen.totales.valor_actual - resumen.totales.valor_cierre >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    ${formatMonto(resumen.totales.valor_actual - resumen.totales.valor_cierre)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <details className="text-sm">
            <summary className="cursor-pointer text-slate-500">Ver detalle por producto</summary>
            <table className="w-full text-sm mt-2">
              <thead className="text-slate-500 text-left">
                <tr>
                  <th className="p-2">Producto</th>
                  <th className="p-2 text-right">Cantidad</th>
                  <th className="p-2 text-right">Costo al cierre</th>
                  <th className="p-2 text-right">Costo actual</th>
                </tr>
              </thead>
              <tbody>
                {resumen.detalle.map((d) => (
                  <tr key={d.producto_id} className="border-t hover:bg-slate-50 transition-colors">
                    <td className="p-2">{d.producto_nombre}</td>
                    <td className="p-2 text-right">{d.cantidad} {d.unidad_medida}</td>
                    <td className="p-2 text-right">{d.costo_unitario_cierre !== null ? `$${formatMonto(d.costo_unitario_cierre)}` : 'sin costo'}</td>
                    <td className="p-2 text-right">{d.costo_unitario_actual !== null ? `$${formatMonto(d.costo_unitario_actual)}` : 'sin costo'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="p-3">Número</th>
                <th className="p-3">Tipo</th>
                <th className="p-3">Fecha</th>
                <th className="p-3 text-right">Productos</th>
                <th className="p-3">Cargado por</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {cierres.map((c) => (
                <tr key={c.id} className="border-t hover:bg-slate-50 transition-colors">
                  <td className="p-3 font-medium whitespace-nowrap tabular-nums">{c.numero}</td>
                  <td className="p-3 whitespace-nowrap">{c.tipo === 'ajuste' ? 'Ajuste' : 'Cierre'}</td>
                  <td className="p-3 whitespace-nowrap">{formatFecha(c.fecha)}</td>
                  <td className="p-3 text-right tabular-nums">{c.lineas}</td>
                  <td className="p-3 whitespace-nowrap">{c.creado_por}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <button onClick={() => verResumen(c.id)} title="Ver valorización" className="text-slate-400 hover:text-primary-600 transition-colors">
                        <Eye size={16} />
                      </button>
                      <button onClick={() => abrirEdicion(c)} title="Editar cantidades" className="text-slate-400 hover:text-primary-600 transition-colors">
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => eliminarCierre(c.id)} title="Eliminar" className="text-slate-400 hover:text-red-600 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {cierres.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-slate-400">
                    No hay cierres ni ajustes de inventario cargados todavía.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
